import { PhotoUpload } from "@/components/common";
import { Button, Input, Slider, Switch, Textarea } from "@/components/ui";
import type { FieldDef } from "@/types/content";

import type { FieldPreset, FieldValue } from "../../types";
import { getFieldPresets } from "../../utils";
import { FieldShell } from "./FieldShell";

interface FieldProps {
  field: FieldDef;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
}

export const TextField = ({ field, value, onChange }: FieldProps) => {
  const str = typeof value === "string" ? value : "";
  return (
    <FieldShell label={field.label} description={field.description}>
      <Input
        value={str}
        placeholder={field.placeholder ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
};

export const TextareaField = ({ field, value, onChange }: FieldProps) => {
  const str = typeof value === "string" ? value : "";
  return (
    <FieldShell label={field.label} description={field.description}>
      <Textarea
        value={str}
        placeholder={field.placeholder ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
};

export const NumberField = ({ field, value, onChange }: FieldProps) => {
  const num = typeof value === "number" ? value : "";

  const isSliderEligible =
    field.type === "slider" ||
    (typeof field.min === "number" && typeof field.max === "number");
  const safeMin = field.min ?? 0;
  const safeMax = field.max ?? 100;

  return (
    <FieldShell label={field.label} description={field.description}>
      {isSliderEligible ? (
        <div className="flex items-center gap-2">
          <Slider
            min={safeMin}
            max={safeMax}
            step={1}
            value={[typeof num === "number" ? num : safeMin]}
            onValueChange={([next]) => onChange(next)}
            className="flex-1"
          />
          <span className="w-8 text-right font-medium text-foreground text-sm tabular-nums">
            {num}
          </span>
        </div>
      ) : (
        <Input
          type="number"
          value={num}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          placeholder={field.placeholder ?? undefined}
          onChange={(e) => {
            const next = e.currentTarget.valueAsNumber;
            onChange(Number.isNaN(next) ? undefined : next);
          }}
        />
      )}
    </FieldShell>
  );
};

export const SelectField = ({ field, value, onChange }: FieldProps) => (
  <FieldShell label={field.label} description={field.description}>
    <div className="flex flex-wrap gap-2">
      {(field.options ?? []).map((opt) => (
        <Button
          key={String(opt.value)}
          variant={value === opt.value ? "default" : "outline"}
          onClick={() => onChange(opt.value as FieldValue)}
          className="capitalize"
        >
          {opt.label}
        </Button>
      ))}
    </div>
  </FieldShell>
);

export const PresetButtons = ({
  presets,
  value,
  onSelect,
}: {
  presets: FieldPreset[];
  value: FieldValue;
  onSelect: (value: string) => void;
}) =>
  presets.map(({ value: v, label, icon: Icon, description }) => (
    <Button
      key={v}
      variant={value === v ? "default" : "outline"}
      onClick={() => onSelect(v)}
    >
      <span className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </span>
      {description && (
        <span className="text-muted-foreground text-xs">{description}</span>
      )}
    </Button>
  ));

export const PresetField = ({ field, value, onChange }: FieldProps) => (
  <FieldShell label={field.label} description={field.description}>
    <div className="flex flex-wrap gap-2">
      <PresetButtons presets={getFieldPresets(field)} value={value} onSelect={onChange} />
    </div>
  </FieldShell>
);

export const BooleanField = ({ field, value, onChange }: FieldProps) => (
  <label className="flex cursor-pointer items-center justify-between gap-2.5">
    <FieldShell label={field.label} description={field.description} />
    <Switch checked={!!value} onCheckedChange={onChange} />
  </label>
);

export const ColorField = ({ field, value, onChange }: FieldProps) => {
  const color = value && typeof value === "string" ? value : "#000000";
  return (
    <FieldShell label={field.label} description={field.description}>
      <Input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-16 cursor-pointer p-1"
      />
    </FieldShell>
  );
};

export const ImageField = ({ field, value, onChange }: FieldProps) => {
  const str = typeof value === "string" ? value : "";
  return (
    <FieldShell label={field.label} description={field.description}>
      <PhotoUpload
        values={str ? [str] : []}
        onChange={(arr) => onChange(arr[0])}
        hint={field.placeholder ?? undefined}
      />
    </FieldShell>
  );
};
